import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { useAppStore } from "../../store/app";
import { listAllCandidates, readProfileJson, type Profile } from "../../lib/api/profile";
import { readAllVacancies, type Vacancy } from "../../lib/api/vacancies";
import { readAllWorkflows, createWorkflow, type Workflow } from "../../lib/api/workflows";
import { calculatePreMatch } from "../../lib/matching/pre-matcher";

interface CandidateRow {
  folder: string;
  name: string;
  workflows: Workflow[];
  profile: Profile | null;
}

type MatchScore = "strong" | "good" | "partial" | "weak" | "no_fit";

const SCORE_COLORS: Record<MatchScore, string> = {
  strong: "bg-emerald-100 text-emerald-800 border-emerald-200",
  good: "bg-sky-100 text-sky-800 border-sky-200",
  partial: "bg-amber-100 text-amber-800 border-amber-200",
  weak: "bg-orange-100 text-orange-800 border-orange-200",
  no_fit: "bg-rose-100 text-rose-700 border-rose-200",
};

const SCORE_LABEL: Record<MatchScore, string> = {
  strong: "Strong",
  good: "Good",
  partial: "Partial",
  weak: "Weak",
  no_fit: "No fit",
};

export default function AdminDashboard() {
  const repoPath = useAppStore((s) => s.repoPath);
  const user = useAppStore((s) => s.user);
  const setActiveCandidateFolder = useAppStore((s) => s.setActiveCandidateFolder);
  const [, navigate] = useLocation();

  const [candidates, setCandidates] = useState<CandidateRow[]>([]);
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingCell, setStartingCell] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hideNoMatches, setHideNoMatches] = useState(true); // By default, hide columns with no active workflows or potential matches
  const [portalFilter, setPortalFilter] = useState("all");

  const load = useCallback(async () => {
    if (!repoPath) return;
    setLoading(true);
    try {
      const [cands, vacs] = await Promise.all([
        listAllCandidates(repoPath),
        readAllVacancies(repoPath),
      ]);

      const rows = await Promise.all(
        cands.map(async (c) => {
          const workflows = await readAllWorkflows(repoPath, c.folder);
          let profile = null;
          try {
            profile = await readProfileJson(repoPath, c.folder);
          } catch (e) {
            console.warn(`No profile for ${c.folder}`, e);
          }
          return { ...c, workflows, profile };
        }),
      );

      setCandidates(rows);
      setVacancies(vacs);
    } catch (err) {
      console.error("Admin dashboard load error", err);
    } finally {
      setLoading(false);
    }
  }, [repoPath]);

  useEffect(() => { load(); }, [load]);

  async function handleStartWorkflow(candidateFolder: string, vacancyId: string) {
    if (!repoPath || !user?.oid) return;
    const cellKey = `${candidateFolder}:${vacancyId}`;
    setStartingCell(cellKey);
    try {
      const newWf = await createWorkflow(repoPath, candidateFolder, user.oid, vacancyId);
      setCandidates((prev) =>
        prev.map((c) =>
          c.folder === candidateFolder
            ? { ...c, workflows: [...c.workflows, newWf] }
            : c,
        ),
      );
    } catch (err) {
      console.error("Failed to start workflow", err);
    } finally {
      setStartingCell(null);
    }
  }

  function handleOpenWorkflow(candidateFolder: string, workflowId: string) {
    setActiveCandidateFolder(candidateFolder);
    navigate(`/workflows/${workflowId}`);
  }

  // Best-matches-per-vacancy: for each vacancy, sort candidates by score
  const scoreOrder: MatchScore[] = ["strong", "good", "partial", "weak", "no_fit"];

  function getPotentialMatchForCell(candidate: CandidateRow, vacancy: Vacancy): MatchScore {
    const wf = getWorkflowForCell(candidate, vacancy.id);
    if (wf?.match_score) return wf.match_score as MatchScore;
    
    if (candidate.profile) {
      return calculatePreMatch(candidate.profile, vacancy).score;
    }
    return "no_fit";
  }

  // Get all unique portals for the dropdown filter
  const portals = Array.from(
    new Set(vacancies.flatMap(v => v.routes.map(r => r.portal).filter(Boolean)))
  );

  // Filter vacancies
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filteredVacancies = vacancies.filter((v) => {
    // Exclude expired vacancies (deadline < today)
    const earliestDeadline = v.routes.reduce((earliest, r) => {
      return !earliest || new Date(r.deadline) < new Date(earliest) ? r.deadline : earliest;
    }, "");
    if (earliestDeadline && new Date(earliestDeadline) < today) {
      return false;
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchTitle = v.title.toLowerCase().includes(q);
      const matchClient = v.client.toLowerCase().includes(q);
      if (!matchTitle && !matchClient) return false;
    }

    if (portalFilter !== "all") {
      const matchesPortal = v.routes.some(r => r.portal === portalFilter);
      if (!matchesPortal) return false;
    }

    if (hideNoMatches) {
      const hasAnyMatch = candidates.some((c) => {
        const score = getPotentialMatchForCell(c, v);
        return ["strong", "good", "partial"].includes(score);
      });
      if (!hasAnyMatch) return false;
    }

    return true;
  });

  const hasActiveWorkflow = (v: Vacancy) => {
    return candidates.some(c => !!getWorkflowForCell(c, v.id));
  };

  const getMinScoreIndex = (v: Vacancy) => {
    let minIdx = 4;
    for (const c of candidates) {
      const score = getPotentialMatchForCell(c, v);
      const idx = scoreOrder.indexOf(score);
      if (idx >= 0 && idx < minIdx) {
        minIdx = idx;
      }
    }
    return minIdx;
  };

  // Sort vacancies by relevance (Active workflows first, followed by best potential match score, then deadline)
  const sortedVacancies = [...filteredVacancies].sort((a, b) => {
    const activeA = hasActiveWorkflow(a);
    const activeB = hasActiveWorkflow(b);
    if (activeA && !activeB) return -1;
    if (!activeA && activeB) return 1;

    const scoreIdxA = getMinScoreIndex(a);
    const scoreIdxB = getMinScoreIndex(b);
    if (scoreIdxA !== scoreIdxB) {
      return scoreIdxA - scoreIdxB;
    }

    const deadlineA = Math.min(...a.routes.map((r) => new Date(r.deadline).getTime()));
    const deadlineB = Math.min(...b.routes.map((r) => new Date(r.deadline).getTime()));
    return deadlineA - deadlineB;
  });

  function getWorkflowForCell(candidate: CandidateRow, vacancyId: string): Workflow | undefined {
    return candidate.workflows.find((w) => w.vacancy_id === vacancyId);
  }

  function getBestMatchesForVacancy(vacancyId: string): { name: string; folder: string; wf: Workflow }[] {
    const matches: { name: string; folder: string; wf: Workflow; scoreIdx: number }[] = [];
    for (const c of candidates) {
      const wf = getWorkflowForCell(c, vacancyId);
      if (wf?.match_score) {
        const idx = scoreOrder.indexOf(wf.match_score as MatchScore);
        matches.push({ name: c.name, folder: c.folder, wf, scoreIdx: idx >= 0 ? idx : 99 });
      }
    }
    return matches.sort((a, b) => a.scoreIdx - b.scoreIdx).map(({ name, folder, wf }) => ({ name, folder, wf }));
  }



  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 space-y-10">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">Match Matrix</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Cross-consultant match overview — {candidates.length} consultant{candidates.length !== 1 ? "s" : ""} and {vacancies.length} vacanc{vacancies.length !== 1 ? "ies" : "y"}
          </p>
        </div>
        <button
          onClick={load}
          className="rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors cursor-pointer focus:outline-none"
        >
          ↻ Refresh
        </button>
      </div>

      {/* Filtering and Search Controls */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white dark:bg-[#182132] p-4.5 rounded-xl border border-slate-200/60 dark:border-slate-800 shadow-xs">
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Vacancy Search */}
          <div className="relative min-w-[240px] flex-1 sm:flex-initial">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search vacancy or client..."
              className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-canvas dark:bg-[#131825] px-3.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 placeholder-slate-405 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-650 cursor-pointer"
              >
                ✕
              </button>
            )}
          </div>

          {/* Portal Filter */}
          <select
            value={portalFilter}
            onChange={(e) => setPortalFilter(e.target.value)}
            className="rounded-lg border border-slate-200 dark:border-slate-800 bg-canvas dark:bg-[#131825] px-3 py-1.5 text-xs text-slate-705 dark:text-slate-300 focus:outline-none cursor-pointer"
          >
            <option value="all">All Portals</option>
            {portals.map(portal => (
              <option key={portal} value={portal}>{portal}</option>
            ))}
          </select>
        </div>

        {/* Hide No Matches Switch */}
        <label className="flex items-center gap-2.5 cursor-pointer text-xs font-semibold text-slate-650 dark:text-slate-350">
          <input
            type="checkbox"
            checked={hideNoMatches}
            onChange={(e) => setHideNoMatches(e.target.checked)}
            className="h-4 w-4 rounded-sm border-slate-300 text-indigo-650 focus:ring-indigo-500 cursor-pointer"
          />
          Show matching vacancies only (Potential matches or active workflows)
        </label>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <span className="h-7 w-7 animate-spin rounded-full border-2 border-slate-300 border-t-slate-800" />
        </div>
      ) : (
        <>
          {/* ── Match Matrix ─────────────────────────────────────────────────── */}
          <section>
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">Match Matrix</h2>
            <div className="rounded-2xl border border-slate-200/70 bg-white shadow-xs overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[640px]">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-wider text-slate-400 w-40 bg-slate-50/60 sticky left-0 z-10">
                      Consultant
                    </th>
                    {sortedVacancies.map((v) => {
                      const deadline = Math.min(...v.routes.map((r) => new Date(r.deadline).getTime()));
                      const daysLeft = Math.ceil((deadline - Date.now()) / 86400000);
                      const urgent = daysLeft <= 3;
                      return (
                        <th key={v.id} className="py-3 px-3 text-[10px] font-bold tracking-wide text-slate-700 min-w-[140px] border-l border-slate-100">
                          <div className="flex flex-col gap-0.5">
                            <span className="truncate max-w-[130px] font-semibold">{v.title}</span>
                            <span className="text-slate-400 font-normal">{v.client}</span>
                            <span className={`text-[9px] font-bold ${urgent ? "text-rose-500" : "text-slate-400"}`}>
                              {daysLeft > 0 ? `${daysLeft}d left` : "Expired"}
                            </span>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {candidates.map((c) => (
                    <tr key={c.folder} className="hover:bg-slate-50/40">
                      <td className="py-3 px-4 text-xs font-semibold text-slate-800 bg-slate-50/60 sticky left-0 z-10 border-r border-slate-100">
                        {c.name}
                      </td>
                      {sortedVacancies.map((v) => {
                        const wf = getWorkflowForCell(c, v.id);
                        const cellKey = `${c.folder}:${v.id}`;
                        const isStarting = startingCell === cellKey;

                        if (wf) {
                          const score = wf.match_score as MatchScore | null;
                          return (
                            <td key={v.id} className="py-2 px-3 border-l border-slate-100">
                              <button
                                onClick={() => handleOpenWorkflow(c.folder, wf.id)}
                                className={`w-full rounded-lg border px-2 py-1.5 text-[10px] font-bold transition-all cursor-pointer focus:outline-none hover:opacity-80 ${
                                  score ? SCORE_COLORS[score] : "bg-slate-100 text-slate-500 border-slate-200"
                                }`}
                              >
                                {score ? SCORE_LABEL[score] : "In progress"}
                              </button>
                            </td>
                          );
                        }

                        const potentialScore = getPotentialMatchForCell(c, v);
                        const hasProfile = !!c.profile;

                        const POTENTIAL_COLORS: Record<MatchScore, string> = {
                          strong: "border-emerald-400/80 text-emerald-700 bg-emerald-50/15 hover:bg-emerald-50/45 dark:bg-emerald-500/10 dark:text-emerald-400",
                          good: "border-sky-400/80 text-sky-700 bg-sky-50/15 hover:bg-sky-50/45 dark:bg-sky-500/10 dark:text-sky-400",
                          partial: "border-amber-400/80 text-amber-700 bg-amber-50/15 hover:bg-amber-50/45 dark:bg-amber-500/10 dark:text-amber-400",
                          weak: "border-orange-300 text-orange-600 bg-orange-50/10 hover:bg-orange-50/35 dark:bg-orange-500/5 dark:text-orange-400",
                          no_fit: "border-slate-200 text-slate-400 bg-slate-50/10 hover:border-slate-300 dark:border-slate-800 dark:text-slate-600",
                        };

                        const labelText = isStarting 
                          ? "…" 
                          : potentialScore !== "no_fit" 
                            ? `Potential: ${potentialScore}` 
                            : "+ Start";

                        return (
                          <td key={v.id} className="py-2 px-3 border-l border-slate-100">
                            <button
                              disabled={isStarting}
                              onClick={() => handleStartWorkflow(c.folder, v.id)}
                              className={`w-full rounded-lg border border-dashed px-2 py-1.5 text-[10px] font-bold transition-all cursor-pointer focus:outline-none disabled:cursor-wait ${
                                POTENTIAL_COLORS[potentialScore]
                              }`}
                              title={!hasProfile ? "Upload CV first to see potential matches" : undefined}
                            >
                              {labelText}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Best Matches Per Vacancy ─────────────────────────────────────── */}
          <section>
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">Best Matches Per Vacancy</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {sortedVacancies.map((v) => {
                const ranked = getBestMatchesForVacancy(v.id);
                return (
                  <div key={v.id} className="rounded-2xl border border-slate-200/70 bg-white shadow-xs p-4">
                    <div className="border-b border-slate-100 pb-3 mb-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{v.client}</p>
                      <p className="text-sm font-semibold text-slate-800 mt-0.5 leading-tight">{v.title}</p>
                    </div>
                    {ranked.length === 0 ? (
                      <p className="text-[11px] text-slate-400 italic">No workflows yet</p>
                    ) : (
                      <ol className="space-y-2">
                        {ranked.map(({ name, folder, wf }, idx) => {
                          const score = wf.match_score as MatchScore;
                          return (
                            <li key={folder} className="flex items-center gap-2">
                              <span className="text-[10px] font-bold text-slate-400 w-4 shrink-0">
                                {idx + 1}.
                              </span>
                              <span className="text-xs font-semibold text-slate-700 flex-1 truncate">{name}</span>
                              <button
                                onClick={() => handleOpenWorkflow(folder, wf.id)}
                                className={`rounded-md border px-2 py-0.5 text-[10px] font-bold cursor-pointer focus:outline-none hover:opacity-80 ${
                                  score ? SCORE_COLORS[score] : "bg-slate-100 text-slate-500 border-slate-200"
                                }`}
                              >
                                {score ? SCORE_LABEL[score] : "—"}
                              </button>
                            </li>
                          );
                        })}
                      </ol>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
